import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  Attachment,
  DocumentationVersion,
  SortDirectionEnum,
  useEdmDocumentationVersionsQuery,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import getFullName from '@lib/utils/getFullName';
import { PER_PAGE_KEY_DEFAULT } from '@lib/enums/urls';
import HistoryCard from './HistoryCard/HistoryCard';
import styles from './DocumentViewHistory.module.scss';
import getAvatarUrl from './utils/getAvatarUrl';
import getVersionActionLabel from './utils/getVersionActionLabel';
import useInfinitePagination from './utils/useInfinitePagination';

interface Props {
  documentationId: string;
  filePermissions: { allowDownload: boolean; allowPrint: boolean };
}

export default function HistoryVersionsTab({
  documentationId,
  filePermissions,
}: Props) {
  const { t } = useTranslation();
  // Holds the whole version (not just its attachment) so the open can be
  // recorded as a view of that specific version.
  const [fileViewerVersion, setFileViewerVersion] =
    useState<DocumentationVersion | null>(null);

  const [page, setPage] = useState(1);

  const { data, loading, previousData } = useEdmDocumentationVersionsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      documentationId,
      page,
      limit: +PER_PAGE_KEY_DEFAULT,
      sorts: { createdAt: SortDirectionEnum.Desc },
    },
  });

  const queryData = data || previousData;
  const newVersions = useMemo(
    () =>
      (_get(queryData, 'data.collection', []) || []) as DocumentationVersion[],
    [queryData],
  );
  const totalPages = _get(queryData, 'data.metadata.totalPages', 1) as number;
  const firstLoading = loading && !previousData;

  const { items: allVersions, hasMore } =
    useInfinitePagination<DocumentationVersion>({
      newItems: newVersions,
      totalPages,
      currentPage: page,
    });

  if (firstLoading) return <CircleLoader />;

  if (!allVersions.length && !loading) {
    return <ListEmptyState title={t('no-data-yet')} text="" />;
  }

  return (
    <CircleLoaderWrapper
      isLoading={loading}
      type={CircleLoaderWrapperType.blur}
    >
      <div className={styles.list}>
        {allVersions.map((version) => {
          const { createdBy, versionNumber, attachmentAttached } = version;
          return (
            <HistoryCard
              key={version.id}
              userName={createdBy ? getFullName(createdBy) : ''}
              avatarUrl={getAvatarUrl(createdBy)}
              actionLabel={getVersionActionLabel(versionNumber, t)}
              date={version.createdAt}
              indentBody={false}
            >
              <DocumentationPreview
                attachment={attachmentAttached}
                notes={version.notes || undefined}
                view={DocumentationPreviewEnum.DocumentRowCard}
                actions={
                  <IconButton
                    icon="visibility"
                    symbolsOutlined
                    type={IconButtonTypes.PrimaryOutlined}
                    onClick={() => setFileViewerVersion(version)}
                  />
                }
              />
            </HistoryCard>
          );
        })}
        {hasMore && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
      <FileViewerModal
        attachment={fileViewerVersion?.attachmentAttached as Attachment}
        documentationVersionId={fileViewerVersion?.id}
        isOpen={fileViewerVersion !== null}
        onClose={() => setFileViewerVersion(null)}
        useDownloadTool={filePermissions.allowDownload}
        usePrintTool={filePermissions.allowPrint}
      />
    </CircleLoaderWrapper>
  );
}
